import { useState, useMemo, useEffect, useCallback } from "react";
import { X, Check, Search, AlertCircle, ArrowRight, Lock, ChevronDown, Info } from 'lucide-react';
import { usePostApi } from "../../hooks/post_api_hook/usePostApi";
import { url } from "../../utils/confserveur";
import { useParams, useNavigate } from "react-router-dom";

/**
 * Modal de synchronisation GAR — le GUICHET DES EXCEPTIONS (spec §5) :
 * il LIE une fiche existante à un compte ENT, il ne crée jamais (le back
 * refuse le type 'create' depuis la refonte fiche unique — un élève sans
 * fiche est un ENTRANT → import établissement).
 * Sections : Correspondances détectées, Élèves sans fiche, En attente.
 */
const ModalSynchroGar = ({ handleClose, show, students, groupNom }) => {
    const navigate = useNavigate();
    const { idclasse } = useParams();

    // ─── States ───
    const [associations, setAssociations] = useState({});
    const [editingStudent, setEditingStudent] = useState(null);
    const [searchManual, setSearchManual] = useState('');
    const [showUnrelated, setShowUnrelated] = useState(false);
    const [{ isLoading: isSyncing }, doPost] = usePostApi();

    // Tous les élèves à synchroniser
    const elevesPourSynchro = useMemo(() =>
        students ? [...(students.pending || []), ...(students.newFromGAR || [])] : [],
    [students]);

    // ─── Helper : est-ce que deux élèves se ressemblent ? ───
    const looksRelated = useCallback((garStudent, candidate) => {
        if (!garStudent || !candidate) return false;
        const normalize = s => (s || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const gNom = normalize(garStudent.nom);
        const gPrenom = normalize(garStudent.prenom);
        const cNom = normalize(candidate.nom);
        const cPrenom = normalize(candidate.prenom);
        return gNom.includes(cNom) || cNom.includes(gNom)
            || gPrenom.includes(cPrenom) || cPrenom.includes(gPrenom);
    }, []);

    // ─── Initialisation : pré-cocher correspondances + newFromGAR (classe) ───
    useEffect(() => {
        if (!show) {
            setAssociations({});
            return;
        }
        const auto = {};

        // Pending : tri par score décroissant, sans doublons d'élèves locaux
        if (students?.pending?.length) {
            const sorted = [...students.pending].sort((a, b) => b.similarity - a.similarity);
            const usedTempIds = new Set();
            sorted.forEach(student => {
                const garId = student.garId || student.id;
                if (usedTempIds.has(student.tempId)) return;
                usedTempIds.add(student.tempId);
                auto[garId] = {
                    studentId: garId,
                    type: 'suggested',
                    tempId: student.tempId,
                    student: students.notConnected?.find(s => s.id === student.tempId) || null,
                };
            });
        }

        // (Plus de pré-cochage 'create' des élèves sans fiche : le guichet lie,
        // il ne crée jamais — spec §5. Un élève sans fiche = un entrant.)

        setAssociations(auto);
    }, [show, students, groupNom]);

    // Reset sub-modal state quand il s'ouvre
    useEffect(() => {
        if (editingStudent) {
            setShowUnrelated(false);
            setSearchManual('');
        }
    }, [editingStudent]);

    // ─── Élèves disponibles pour association manuelle ───
    const getAvailableStudents = useCallback((currentGarId) => {
        if (!students?.notConnected) return [];
        const usedIds = Object.entries(associations)
            .filter(([garId]) => garId !== currentGarId)
            .map(([, a]) => a.tempId);
        let available = students.notConnected.filter(s => !usedIds.includes(s.id));
        if (groupNom) {
            const current = elevesPourSynchro.find(e => (e.garId || e.id) === currentGarId);
            if (current?.classe_nom) {
                available = available.filter(s => s.classe_nom === current.classe_nom);
            }
        }
        return available;
    }, [students, associations, elevesPourSynchro, groupNom]);

    // ─── Toggle checkbox ───
    const handleToggle = useCallback((garId, student) => {
        if (student.similarity === 100) return;

        setAssociations(prev => {
            if (prev[garId]) {
                const next = { ...prev };
                delete next[garId];
                return next;
            }
            const isPending = students?.pending?.some(s => (s.garId || s.id) === garId);
            if (isPending) {
                const alreadyUsed = Object.values(prev).some(a => a.tempId === student.tempId);
                if (alreadyUsed) return prev;
                return {
                    ...prev,
                    [garId]: {
                        studentId: garId,
                        type: 'suggested',
                        tempId: student.tempId,
                        student: students.notConnected?.find(s => s.id === student.tempId) || null,
                    },
                };
            }
            // Élève sans fiche : pas de cochage direct — seule l'association
            // manuelle (bouton Associer → type 'existing') est possible.
            return prev;
        });
    }, [students]);

    // ─── Association manuelle ───
    const handleManualAssociate = useCallback((garId, selectedStudent) => {
        setAssociations(prev => ({
            ...prev,
            [garId]: { studentId: garId, type: 'existing', tempId: selectedStudent.id, student: selectedStudent },
        }));
        setEditingStudent(null);
    }, []);

    // ─── Annuler une association manuelle (groupe) ───
    const handleCancelAssociation = useCallback((garId) => {
        setAssociations(prev => {
            const next = { ...prev };
            delete next[garId];
            return next;
        });
    }, []);

    // ─── Synchronisation ───
    const handleConfirmSync = useCallback(async () => {
        try {
            const result = await doPost(
                { associations, classe_id: idclasse },
                `${url}eleves-gar/bulk_sync_eleves_gar/`
            );
            if (result.success) {
                const { succes, echecs } = result.data.details;
                if (echecs === 0) alert(`Parfait ! ${succes} élève(s) synchronisé(s) avec succès !`);
                else alert(`Synchronisation terminée : ${succes} réussite(s), ${echecs} échec(s)`);
                handleClose();
                setAssociations({});
                navigate(0);
            } else {
                alert(`Erreur : ${result.error?.response?.data?.error || 'Erreur inconnue'}`);
            }
        } catch {
            alert('Une erreur inattendue s\'est produite. Veuillez réessayer.');
        }
    }, [associations, idclasse, doPost, handleClose, navigate]);

    // ─── Compteurs ───
    const counts = useMemo(() => ({
        correspondances: students?.pending?.length || 0,
        nouveaux: students?.newFromGAR?.length || 0,
        enAttente: (students?.notConnected || []).filter(s =>
            !Object.values(associations).some(a => a.tempId === s.id)
        ).length,
        selected: Object.keys(associations).length,
    }), [students, associations]);

    if (!show) return null;

    // ─── Rendu d'un élève dans le sub-modal ───
    const renderSubModalStudent = (student, garStudent) => {
        const related = looksRelated(garStudent, student);
        return (
            <button
                key={student.id}
                onClick={() => handleManualAssociate(editingStudent, student)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition text-left ${
                    related ? 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50' : 'border-gray-100 hover:border-orange-300 hover:bg-orange-50/30'
                }`}
            >
                <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {student.initials}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800">
                        {student.prenom} {student.nom}
                        {groupNom && student.classe_nom && (
                            <span className="text-[10px] text-gray-400 ml-1.5">{student.classe_nom}</span>
                        )}
                    </div>
                </div>
            </button>
        );
    };

    return (
        <>
            {/* Overlay */}
            <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>

                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-500 to-cyan-500 px-6 py-5 text-white flex-shrink-0">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-bold">Synchronisation ENT</h2>
                                <p className="text-sm text-white/80 mt-0.5">Vérifiez les correspondances et synchronisez vos élèves</p>
                            </div>
                            <button onClick={handleClose} className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition">
                                <X size={16} />
                            </button>
                        </div>
                        <div className="flex gap-3 mt-4">
                            {[
                                { label: 'Correspondances', value: counts.correspondances },
                                { label: 'Sans fiche', value: counts.nouveaux },
                                { label: 'En attente', value: counts.enAttente },
                            ].map(s => (
                                <div key={s.label} className="flex-1 bg-white/20 rounded-xl px-3 py-2">
                                    <div className="text-[11px] text-white/70">{s.label}</div>
                                    <div className="text-xl font-bold">{s.value}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Body */}
                    <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

                        {/* ═══ Correspondances détectées ═══ */}
                        {(students?.pending?.length > 0) && (
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <h3 className="text-sm font-bold text-gray-800">Correspondances détectées</h3>
                                        <p className="text-[11px] text-gray-400">Pré-sélectionnées : décochez ou modifiez si besoin</p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            const allChecked = students.pending.every(s => associations[s.garId || s.id]);
                                            if (allChecked) {
                                                setAssociations(prev => {
                                                    const next = {};
                                                    // Garder 100% + newFromGAR
                                                    Object.entries(prev).forEach(([garId, a]) => {
                                                        const s = students.pending.find(p => (p.garId || p.id) === garId);
                                                        if (s?.similarity === 100) next[garId] = a;
                                                        if (!students.pending.some(p => (p.garId || p.id) === garId)) next[garId] = a;
                                                    });
                                                    return next;
                                                });
                                            } else {
                                                const sorted = [...students.pending].sort((a, b) => b.similarity - a.similarity);
                                                setAssociations(prev => {
                                                    const next = { ...prev };
                                                    const usedTempIds = new Set(
                                                        Object.entries(prev)
                                                            .filter(([garId]) => !students.pending.some(p => (p.garId || p.id) === garId))
                                                            .map(([, a]) => a.tempId).filter(Boolean)
                                                    );
                                                    sorted.forEach(s => {
                                                        const garId = s.garId || s.id;
                                                        if (usedTempIds.has(s.tempId)) return;
                                                        usedTempIds.add(s.tempId);
                                                        if (!next[garId]) {
                                                            next[garId] = {
                                                                studentId: garId, type: 'suggested', tempId: s.tempId,
                                                                student: students.notConnected?.find(nc => nc.id === s.tempId) || null,
                                                            };
                                                        }
                                                    });
                                                    return next;
                                                });
                                            }
                                        }}
                                        className="text-[11px] font-medium text-blue-600 hover:text-blue-800 transition"
                                    >
                                        {students.pending.every(s => associations[s.garId || s.id]) ? 'Tout décocher' : 'Tout cocher'}
                                    </button>
                                </div>

                                <div className="space-y-1.5">
                                    {students.pending.map(student => {
                                        const garId = student.garId || student.id;
                                        const isChecked = !!associations[garId];
                                        const isLocked = student.similarity === 100;
                                        const assoc = associations[garId];
                                        const localStudent = assoc?.student || students.notConnected?.find(s => s.id === student.tempId);

                                        return (
                                            <div
                                                key={garId}
                                                onClick={() => handleToggle(garId, student)}
                                                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all cursor-pointer ${
                                                    isChecked ? 'border-blue-200 bg-blue-50/50' : 'border-gray-200 bg-white hover:bg-gray-50'
                                                } ${isLocked ? 'cursor-default' : ''}`}
                                            >
                                                <div className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 transition ${
                                                    isChecked ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'
                                                }`}>
                                                    {isChecked && <Check size={12} className="text-white" />}
                                                </div>
                                                {isLocked && <Lock size={11} className="text-blue-400 flex-shrink-0 -ml-1" />}

                                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                                    <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                                        {student.prenom?.[0]}{student.nom?.[0]}
                                                    </div>
                                                    <div className="truncate">
                                                        <span className="text-sm font-medium text-gray-800">{student.prenom || student.garName?.split(' ')[0]} {student.nom || student.garName?.split(' ')[1]}</span>
                                                        {groupNom && student.classe_nom && <span className="text-[10px] text-gray-400 ml-1.5">{student.classe_nom}</span>}
                                                    </div>
                                                </div>

                                                <ArrowRight size={14} className="text-gray-300 flex-shrink-0" />

                                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                                    <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                                        {localStudent?.initials || '?'}
                                                    </div>
                                                    <span className="text-sm text-gray-600 truncate">
                                                        {localStudent ? `${localStudent.prenom} ${localStudent.nom}` : 'Non trouvé'}
                                                    </span>
                                                </div>

                                                <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-md flex-shrink-0 ${
                                                    student.similarity >= 95 ? 'bg-emerald-100 text-emerald-700'
                                                    : 'bg-amber-100 text-amber-700'
                                                }`}>{student.similarity}%</span>

                                                {!isLocked && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setEditingStudent(garId); }}
                                                        className="text-[11px] text-gray-400 hover:text-blue-600 border border-gray-200 hover:border-blue-300 rounded-md px-2.5 py-1 transition flex-shrink-0"
                                                    >
                                                        Modifier
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* ═══ Élèves sans fiche TanoEd (entrants probables) ═══ */}
                        {(students?.newFromGAR?.length > 0) && (
                            <div>
                                <div className="mb-3">
                                    <h3 className="text-sm font-bold text-amber-700">Élèves connectés via l'ENT, sans fiche TanoEd</h3>
                                    <p className="text-[11px] leading-snug text-gray-600">
                                        {groupNom
                                            ? "Associez-les manuellement à un élève de votre liste. Sinon, leur fiche sera créée lorsque le professeur délégué mettra l'établissement à jour."
                                            : "Ce sont probablement des arrivées. Associez-les manuellement à une fiche existante ci-dessous ; sinon, leur fiche sera créée lorsque le professeur délégué mettra l'établissement à jour."}
                                    </p>
                                </div>

                                <div className="space-y-1.5">
                                    {students.newFromGAR.map(student => {
                                        const garId = student.garId || student.id;
                                        const isChecked = !!associations[garId];
                                        const assoc = associations[garId];

                                        return (
                                            <div
                                                key={garId}
                                                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all ${
                                                    isChecked ? 'border-amber-200 bg-amber-50/50' : 'border-gray-200 bg-white'
                                                }`}
                                            >
                                                {/* Checkbox : cochée uniquement si une association MANUELLE existe
                                                    (le guichet lie, ne crée jamais — plus de création auto) */}
                                                <div className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 ${
                                                    isChecked ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300 bg-white'
                                                }`}>
                                                    {isChecked ? <Check size={12} className="text-white" /> : null}
                                                </div>

                                                <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                                    {student.prenom?.[0]}{student.nom?.[0]}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <span className="text-sm font-medium text-gray-800">{student.prenom} {student.nom}</span>
                                                    {groupNom && student.classe_nom && <span className="text-[10px] text-gray-400 ml-1.5">{student.classe_nom}</span>}

                                                    {/* Badge contexte-dépendant */}
                                                    {assoc?.type === 'existing' && assoc?.student && (
                                                        <span className="text-[11px] text-emerald-600 ml-2">→ {assoc.student.prenom} {assoc.student.nom}</span>
                                                    )}
                                                    {!isChecked && (
                                                        <span className="inline-flex items-center text-[10px] text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full ml-2">
                                                            Sans fiche (à importer)
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Annuler une association manuelle (tous contextes) */}
                                                {assoc?.type === 'existing' && (
                                                    <button
                                                        onClick={() => handleCancelAssociation(garId)}
                                                        className="text-[11px] text-red-400 hover:text-red-600 border border-red-200 hover:border-red-300 rounded-md px-2 py-0.5 transition flex-shrink-0"
                                                        title="Annuler l'association"
                                                    >
                                                        ×
                                                    </button>
                                                )}

                                                <button
                                                    onClick={() => setEditingStudent(garId)}
                                                    className={isChecked && assoc?.type === 'existing'
                                                        ? "text-[11px] text-gray-500 hover:text-blue-700 border border-gray-200 hover:border-blue-300 rounded-md px-2.5 py-1 transition flex-shrink-0"
                                                        : "text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-sm rounded-lg px-3 py-1.5 transition flex-shrink-0"}
                                                >
                                                    {isChecked && assoc?.type === 'existing' ? 'Modifier' : 'Associer'}
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* ═══ En attente GAR ═══ */}
                        {(() => {
                            const remaining = (students?.notConnected || []).filter(s =>
                                !Object.values(associations).some(a => a.tempId === s.id)
                            );
                            if (remaining.length === 0) return null;
                            return (
                                <div>
                                    <h3 className="text-sm font-bold text-gray-500 mb-1">En attente ENT</h3>
                                    <p className="text-[11px] leading-snug text-gray-500 mb-3">Pas encore connectés via l'ENT</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {remaining.map(s => (
                                            <span key={s.id} className="text-xs bg-gray-100 text-gray-500 px-3 py-1.5 rounded-lg">
                                                {s.prenom} {s.nom?.[0]}.
                                                {groupNom && s.classe_nom && <span className="text-gray-400 ml-1">({s.classe_nom})</span>}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}

                        {elevesPourSynchro.length === 0 && (
                            <div className="text-center py-12 text-gray-400">
                                <p className="text-sm font-medium">Aucun élève à synchroniser</p>
                                <p className="text-xs mt-1">Tous les élèves sont déjà synchronisés ou aucun élève connecté via l'ENT n'a été détecté.</p>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
                        <span className="text-sm text-gray-400">
                            {counts.selected} élève{counts.selected > 1 ? 's' : ''} sélectionné{counts.selected > 1 ? 's' : ''}
                        </span>
                        <div className="flex gap-2">
                            <button onClick={handleClose} className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-500 border border-gray-200 hover:bg-gray-50 transition">
                                Annuler
                            </button>
                            <button
                                onClick={handleConfirmSync}
                                disabled={counts.selected === 0 || isSyncing}
                                className={`px-5 py-2.5 rounded-xl text-sm font-bold text-white transition ${
                                    counts.selected > 0 && !isSyncing ? 'bg-blue-600 hover:bg-blue-700 shadow-sm' : 'bg-gray-300 cursor-not-allowed'
                                }`}
                            >
                                {isSyncing ? 'Synchronisation...' : `Synchroniser (${counts.selected})`}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ═══ Sub-modal : Association manuelle (2 groupes) ═══ */}
            {editingStudent && (
                <div
                    className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => setEditingStudent(null)}
                >
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[70vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                        {/* Header */}
                        <div className="px-5 py-4 border-b border-gray-100 flex-shrink-0">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-bold text-gray-800">Associer à un élève</h3>
                                <button onClick={() => setEditingStudent(null)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition">
                                    <X size={14} />
                                </button>
                            </div>
                            {(() => {
                                const garStudent = elevesPourSynchro.find(e => (e.garId || e.id) === editingStudent);
                                return garStudent ? (
                                    <>
                                        <p className="text-xs leading-snug text-gray-500 mt-1">
                                            Élève ENT : <span className="font-semibold text-gray-800">{garStudent.prenom} {garStudent.nom}</span>
                                            {groupNom && garStudent.classe_nom && <span className="text-gray-400"> ({garStudent.classe_nom})</span>}
                                        </p>
                                        <p className="text-[11px] leading-snug text-gray-500 mt-1">
                                            Cliquez ci-dessous sur l'élève de votre liste qui lui correspond.
                                        </p>
                                    </>
                                ) : null;
                            })()}
                            <div className="relative mt-3">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                <input
                                    type="text"
                                    placeholder="Rechercher un élève..."
                                    value={searchManual}
                                    onChange={e => setSearchManual(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-blue-200 focus:border-blue-300 focus:outline-none"
                                />
                            </div>
                        </div>

                        {/* Liste en 2 groupes */}
                        <div className="flex-1 overflow-y-auto p-3">
                            {(() => {
                                const available = getAvailableStudents(editingStudent);
                                const garStudent = elevesPourSynchro.find(e => (e.garId || e.id) === editingStudent);
                                const term = searchManual.toLowerCase().trim();
                                const filtered = term
                                    ? available.filter(s => `${s.prenom} ${s.nom}`.toLowerCase().includes(term))
                                    : available;

                                const related = filtered.filter(s => looksRelated(garStudent, s));
                                const unrelated = filtered.filter(s => !looksRelated(garStudent, s));

                                return (
                                    <>
                                        {filtered.length === 0 && (
                                            <p className="text-center text-xs text-gray-400 py-2">
                                                {term ? 'Aucun élève ne correspond à votre recherche.' : 'Aucun élève disponible pour le moment.'}
                                            </p>
                                        )}

                                        {/* Groupe 1 : ressemblants — toujours visible, présentés comme suggestion */}
                                        {related.length > 0 && (
                                            <div className="space-y-1 mb-3">
                                                <p className="text-[11px] font-semibold text-emerald-600 px-1 mb-1">C'est peut-être…</p>
                                                {related.map(s => renderSubModalStudent(s, garStudent))}
                                            </div>
                                        )}

                                        {/* Groupe 2 : non-ressemblants — chevron fermé par défaut */}
                                        {unrelated.length > 0 && (
                                            <div>
                                                <button
                                                    onClick={() => setShowUnrelated(prev => !prev)}
                                                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-orange-50 border border-orange-200 text-left"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <AlertCircle size={14} className="text-orange-500 flex-shrink-0" />
                                                        <span className="text-xs font-medium text-orange-700">
                                                            Ces élèves ne semblent pas correspondre ({unrelated.length})
                                                        </span>
                                                    </div>
                                                    <ChevronDown size={16} className={`text-orange-400 transition-transform ${showUnrelated ? 'rotate-180' : ''}`} />
                                                </button>
                                                {showUnrelated && (
                                                    <div className="space-y-1 mt-1.5">
                                                        {unrelated.map(s => renderSubModalStudent(s, garStudent))}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {related.length === 0 && (
                                            <div className="mt-2">
                                                {!showUnrelated && unrelated.length > 0 && (
                                                    <p className="text-center text-xs text-gray-400 pb-2">
                                                        Aucune suggestion directe : ouvrez la liste ci-dessus.
                                                    </p>
                                                )}
                                                <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2.5">
                                                    <Info size={15} className="text-blue-500 flex-shrink-0 mt-0.5" />
                                                    <p className="text-[11px] leading-snug text-blue-800">
                                                        Aucun élève de votre liste ne correspond&nbsp;? S'il s'agit d'un nouvel arrivant, sa fiche sera créée lorsque le professeur délégué mettra l'établissement à jour.
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ModalSynchroGar;
